import {
    Area,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { formatReportAxisCurrency } from 'shared/lib/reportFormatters';

import { ReportWidgetChartTooltip } from './ReportWidgetChartTooltip';
import { ReportWidgetLineChartActiveDot } from './ReportWidgetLineChartActiveDot';
import { ReportWidgetLineChartLegend } from './ReportWidgetLineChartLegend';
import { ReportWidgetLineChartXAxisTick } from './ReportWidgetLineChartXAxisTick';

import { lineChartRightGutter } from '../../model/lib/lineChartRightGutter';
import {
    REPORT_CHART_AXIS_STROKE,
    REPORT_CHART_GRID_STROKE_OPACITY,
} from '../chartColors';

import type {
    ReportWidgetItem,
    ReportWidgetTooltipRowsBuilder,
} from '../../model/types';
import type { CurveType } from 'recharts/types/shape/Curve';

export interface ReportWidgetLineChartDataPoint {
    name: string;
    id: string;
    value1: number;
    value2: number;
}

interface ReportWidgetLineChartProps {
    data: ReportWidgetLineChartDataPoint[];
    showLegend: boolean;
    showSecondSeries?: boolean;
    lineNames: [string, string];
    tooltipFormatter: (value: number) => string;
    tooltipRowsBuilder?: ReportWidgetTooltipRowsBuilder;
    width?: number;
    height?: number;
    lineType?: CurveType;
    xAxisTickAngle?: number;
    disableAnimation?: boolean;
    xAxisTickFormatter?: (
        label: string | number,
        index: number,
        data: ReportWidgetLineChartDataPoint[],
    ) => string;
    yAxisTickFormatter?: (value: number) => string;
    lineColors?: [string, string];
}

const DEFAULT_LINE_COLORS: [string, string] = ['#4BA054', '#B2B2B2'];

function gradientIdForColor(color: string): string {
    return `report-line-fill-${color.replaceAll(/[^a-zA-Z0-9]/g, '')}`;
}

function lineDotStyle(
    color: string,
    active: boolean,
): { r: number; strokeWidth: number; fill: string; stroke: string } {
    if (active) {
        return {
            r: 5,
            strokeWidth: 2,
            fill: color,
            stroke: '#FFFFFF',
        };
    }

    return {
        r: 3,
        strokeWidth: 2,
        fill: '#FFFFFF',
        stroke: color,
    };
}

export const ReportWidgetLineChart = ({
    data,
    showLegend,
    showSecondSeries = true,
    lineNames,
    tooltipFormatter,
    tooltipRowsBuilder,
    width,
    height,
    lineType = 'linear',
    disableAnimation = false,
    xAxisTickAngle,
    xAxisTickFormatter,
    yAxisTickFormatter,
    lineColors = DEFAULT_LINE_COLORS,
}: ReportWidgetLineChartProps) => {
    const primaryColor = lineColors[0];
    const lastIndex = data.length - 1;
    const lastRawLabel = data[lastIndex]?.name ?? '';
    const lastLabel = xAxisTickFormatter
        ? xAxisTickFormatter(lastRawLabel, lastIndex, data)
        : lastRawLabel;
    const rightMargin = xAxisTickAngle ? 10 : lineChartRightGutter(lastLabel);
    const fillId = gradientIdForColor(primaryColor);
    const chartData = data.map((point) => ({
        ...point,
        value1Fill: point.value1,
    }));
    const gradientCoords = {
        id: fillId,
        x1: '0',
        y1: '0',
        x2: '0',
        y2: '1',
    } as const;

    return (
        <ComposedChart
            data={chartData}
            width={width}
            height={height}
            margin={{ top: 5, right: rightMargin, bottom: 5, left: 5 }}
        >
            <defs>
                <linearGradient {...gradientCoords}>
                    <stop
                        offset="0%"
                        stopColor={primaryColor}
                        stopOpacity={0.5}
                    />
                    <stop
                        offset="50%"
                        stopColor={primaryColor}
                        stopOpacity={0.25}
                    />
                    <stop
                        offset="100%"
                        stopColor={primaryColor}
                        stopOpacity={0}
                    />
                </linearGradient>
            </defs>
            <CartesianGrid
                stroke={REPORT_CHART_AXIS_STROKE}
                strokeWidth={1}
                strokeOpacity={REPORT_CHART_GRID_STROKE_OPACITY}
            />
            <XAxis
                dataKey="name"
                type="category"
                scale="point"
                tick={
                    <ReportWidgetLineChartXAxisTick
                        data={data}
                        formatter={xAxisTickFormatter}
                        angle={xAxisTickAngle}
                    />
                }
                tickLine={{ stroke: REPORT_CHART_AXIS_STROKE, strokeWidth: 1 }}
                tickSize={10}
                tickMargin={6}
                axisLine={{ stroke: REPORT_CHART_AXIS_STROKE, strokeWidth: 1 }}
                padding={{ left: 0, right: 0 }}
            />
            <YAxis
                tick={{ fontSize: 12, fill: '#000000' }}
                tickLine={{ stroke: REPORT_CHART_AXIS_STROKE, strokeWidth: 1 }}
                tickSize={10}
                tickMargin={6}
                tickFormatter={(v: number) => {
                    if (yAxisTickFormatter) {
                        return yAxisTickFormatter(v);
                    }

                    if (typeof v !== 'number') {
                        return String(v);
                    }
                    return formatReportAxisCurrency(v);
                }}
                domain={[
                    'auto',
                    (dataMax: number) =>
                        Number.isFinite(dataMax) ? dataMax * 1.2 : dataMax,
                ]}
                axisLine={{ stroke: REPORT_CHART_AXIS_STROKE, strokeWidth: 1 }}
            />
            <Tooltip
                cursor={{ stroke: REPORT_CHART_AXIS_STROKE, strokeWidth: 1 }}
                content={
                    <ReportWidgetChartTooltip
                        tooltipFormatter={tooltipFormatter}
                        total={0}
                        items={[] as ReportWidgetItem[]}
                        tooltipRowsBuilder={tooltipRowsBuilder}
                    />
                }
            />
            {showLegend && (
                <Legend
                    verticalAlign="top"
                    align="left"
                    iconType="circle"
                    content={<ReportWidgetLineChartLegend />}
                />
            )}
            <Area
                type={lineType}
                dataKey="value1Fill"
                fill={`url(#${fillId})`}
                stroke="none"
                isAnimationActive={!disableAnimation}
                legendType="none"
                tooltipType="none"
                dot={false}
                activeDot={false}
            />
            <Line
                type={lineType}
                dataKey="value1"
                name={lineNames[0]}
                stroke={primaryColor}
                strokeWidth={2}
                dot={lineDotStyle(primaryColor, false)}
                activeDot={ReportWidgetLineChartActiveDot}
                isAnimationActive={!disableAnimation}
                legendType="circle"
            />
            {showSecondSeries && (
                <Line
                    type={lineType}
                    dataKey="value2"
                    name={lineNames[1]}
                    stroke={lineColors[1]}
                    strokeWidth={2}
                    dot={lineDotStyle(lineColors[1], false)}
                    activeDot={ReportWidgetLineChartActiveDot}
                    isAnimationActive={!disableAnimation}
                    legendType="circle"
                />
            )}
        </ComposedChart>
    );
};
